import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

function Loader3D() {
  const L = 210, H = 104, D = 96;
  const hL = L / 2, hH = H / 2, hD = D / 2;
  const face = (w, h, t, cls = "", children = null) => (
    <div className={`fc-face fc-panel ${cls}`} style={{ width: w, height: h, marginLeft: -w / 2, marginTop: -h / 2, transform: t }}>
      {children}
    </div>
  );
  const brand = (
    <div className="fc-brand">
      <img src="/brand/cr-mark-v2.png" alt="Conexa Relay" className="fc-brand-mark" />
      <span className="fc-brand-text">CONEXA<span className="text-primary">RELAY</span></span>
    </div>
  );
  return (
    <div data-testid="loading-3d" className="cr-scene relative w-[340px] h-[280px] flex items-center justify-center">
      <div className="cr-ring" />
      <div className="cr-bob">
        <div className="fc-box" style={{ width: L, height: H }}>
          {face(L, H, `translateZ(${hD}px)`, "fc-corr", brand)}
          {face(L, H, `rotateY(180deg) translateZ(${hD}px)`, "fc-corr", brand)}
          {face(D, H, `rotateY(-90deg) translateZ(${hL}px)`, "fc-corr-h")}
          {face(D, H, `rotateY(90deg) translateZ(${hL}px)`, "", (
            <>
              <div className="fc-door-half" style={{ left: 8 }} />
              <div className="fc-door-half" style={{ right: 8 }} />
            </>
          ))}
          {face(L, D, `rotateX(90deg) translateZ(${hH}px)`, "fc-corr")}
          {face(L, D, `rotateX(-90deg) translateZ(${hH}px)`, "fc-corr")}
        </div>
      </div>
    </div>
  );
}

export default function LoadingScreen({ onDone }) {
  const [count, setCount] = useState(0);
  const [show, setShow] = useState(true);

  useEffect(() => {
    const t = setInterval(() => {
      setCount((c) => (c >= 100 ? 100 : Math.min(100, c + Math.floor(Math.random() * 9) + 4)));
    }, 110);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (count >= 100) {
      const t = setTimeout(() => { setShow(false); onDone?.(); }, 550);
      return () => clearTimeout(t);
    }
  }, [count, onDone]);

  // Hard fallback: always dismiss within ~3.8s regardless of timer throttling.
  useEffect(() => {
    const cap = setTimeout(() => { setShow(false); onDone?.(); }, 3800);
    return () => clearTimeout(cap);
  }, [onDone]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="loading-screen"
          className="fixed inset-0 z-[100] bg-[#020202] flex flex-col items-center justify-center"
          exit={{ y: "-100%" }}
          transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
        >
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <motion.span key={i} className="absolute w-1 h-1 rounded-full bg-primary/40"
                style={{ left: `${(i * 37) % 100}%`, top: `${(i * 53) % 100}%` }}
                animate={{ opacity: [0.1, 0.8, 0.1], scale: [1, 1.8, 1] }}
                transition={{ duration: 2 + (i % 5), repeat: Infinity, delay: i * 0.1 }} />
            ))}
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}>
            <Loader3D />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="-mt-2 font-heading font-extrabold text-3xl md:text-5xl tracking-tighter text-white">
            CONEXA <span className="text-primary">RELAY</span>
          </motion.div>
          <div className="mt-4 font-mono-x text-primary text-xs tracking-[0.35em]">
            DISPATCHING THE NETWORK — {count}%
          </div>
          <div className="mt-4 h-[2px] w-56 bg-white/10 overflow-hidden rounded-full">
            <div className="h-full bg-primary transition-all duration-100" style={{ width: `${count}%` }} />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
